import React from 'react';
import { Headercomponent } from '../components/headercomponent';
import NodeBackground from '../components/nodeBackground';
import { Link } from 'react-router-dom';
import { Footer } from '../components/Footer';

function Resources() {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalTitle, setModalTitle] = React.useState('');
  const featuredResources = [
    {
      label: 'Start here',
      title: 'What Is OSINT and Why Does It Matter?',
      description:
        'A plain-language guide to open-source intelligence, how it is used in protective intelligence, and why public information can shape security decisions.',
      button: 'Read the OSINT primer',
      path: '/resources/what-is-osint',
    },
    {
      label: 'Protective Intelligence',
      title: 'How does OSINT inform your protective posture?',
      description:
        'Information available online can be used to assess and inform protective posture based on movement, events, and patterns of life that shape exposure and risk.',
      button: 'Read about OSINT threat assessments',
      path: '/reports',
    },
    {
      label: 'Case study',
      title: 'Crime Mapping for Property and Travel Decisions',
      description:
        'See how public crime data can be converted into practical intelligence for home buyers, travelers, advisors, and protective teams.',
      button: 'Explore crime intelligence',
      path: '/risk-explorer',
    },
  ];

  const guideCollections = [
    {
      eyebrow: 'OSINT informational guides',
      title: 'Understand the Risks People Search for First',
      intro:
        'Educational guides written for executives, families, advisors, investigators, and security professionals who need clear explanations without jargon.',
      articles: [
        {
          title: 'What Is OSINT?',
          audience: 'Executives, founders, security teams',
          summary:
            'A beginner-friendly explanation of open-source intelligence, common public sources, and how OSINT supports risk decisions.',
          path: '/resources/what-is-osint',
        },
        {
          title: 'What Is Doxxing?',
          audience: 'Public figures, executives, families',
          summary:
            'How personal information is exposed online, why it matters, and what warning signs should trigger an exposure review.',
          path: '/resources/doxxing-guide',
        },
        {
          title: 'What Is Swatting?',
          audience: 'High-profile clients, family offices, security teams',
          summary:
            'An overview of swatting risk, public exposure indicators, and why online context can matter for prevention and response planning.',
          path: '/resources/swatting-guide',
        },
        {
          title: 'Online Exposure Assessments Explained',
          audience: 'Executives, founders, public-facing professionals',
          summary:
            'What an exposure assessment reviews, including people-search sites, social profiles, public records, and household visibility.',
          path: '/resources/online-exposure-assessment',
        },
        {
          title: 'Data Brokers and People-Search Sites',
          audience: 'Families, advisors, privacy-conscious clients',
          summary:
            'How data brokers collect and display personal information and why removal can be part of a larger protective strategy.',
          path: '/resources/data-brokers',
        },
        {
          title: 'Threat Monitoring vs Keyword Alerts',
          audience: 'Corporate security, EP teams, OSINT analysts',
          summary:
            'Why keyword matches create noise and how analyst-led review can help identify comments, narratives, and indicators that matter.',
          path: '/resources/threat-monitoring-vs-keyword-alerts',
        },
      ],
    },
    {
      eyebrow: 'Client decision guides',
      title: 'Resources by Use Case',
      intro:
        'Guides that connect OSINT, crime data, reports, and services to the decisions your clients are already trying to make.',
      articles: [
        {
          title: 'How OSINT Supports Executive Protection',
          audience: 'Executive protection teams',
          summary:
            'How online threat detection, exposure reviews, travel intelligence, and event monitoring support protective operations.',
          path: '/resources/osint-for-executive-protection',
        },
        {
          title: 'Travel Risk Briefs for Executives and Families',
          audience: 'Travelers, advisors, protection teams',
          summary:
            'What to review before high-value travel, including lodging area risk, routes, protests, crime trends, and local context.',
          path: '/resources/travel-risk-briefs',
        },
        {
          title: 'Crime Intelligence for Home Buyers',
          audience: 'Home buyers, real estate agents, relocation clients',
          summary:
            'Why block-level crime patterns, trend lines, and local hotspots can reveal risk that listing sites do not show.',
          path: '/resources/crime-intelligence-for-home-buyers',
        },
        {
          title: 'OSINT for Event Security Planning',
          audience: 'Event organizers, venues, corporate security',
          summary:
            'How public posts, local crime, protest indicators, venue context, and online chatter can support event planning.',
          path: '/resources/osint-for-event-security',
        },
      ],
    },
  ];

  const osintTools = [
    {
      title: 'Free OSINT Tools for Beginners',
      summary:
        'A curated overview of public search, mapping, archive, username, domain, and social discovery tools that can support basic research.',
      path: '/resources/free-osint-tools',
    },
    {
      title: 'Free Tools for Online Exposure Reviews',
      summary:
        'Public resources that can help identify exposed profiles, usernames, addresses, phone numbers, old posts, and public-facing records.',
      path: '/resources/online-exposure-tools',
    },
    {
      title: 'Free Tools for Crime and Location Research',
      summary:
        'Where to find public crime data, city open-data portals, maps, neighborhood context, and other public safety indicators.',
      path: '/resources/crime-location-research-tools',
    },
    {
      title: 'Free Tools for Website and Domain Research',
      summary:
        'Public tools for domain lookups, DNS context, web archives, page history, and website ownership indicators.',
      path: '/resources/domain-research-tools',
    },
  ];

  const professionalSections = [
    {
      title: 'For Executive Protection Teams',
      text: 'Use these resources to connect online signals, public exposure, travel risk, and event intelligence to protective planning.',
      link: '/industries',
      button: 'View EP resources',
    },
    {
      title: 'For Corporate Security',
      text: 'Learn how OSINT can support executive risk monitoring, workplace threat indicators, reputation concerns, and leadership briefings.',
      link: '/industries',
      button: 'View corporate security fit',
    },
    {
      title: 'For Family Offices and Advisors',
      text: 'Review guides on privacy exposure, travel risk, residential intelligence, and report options for high-value personal decisions.',
      link: '/industries',
      button: 'View family office fit',
    },
    {
      title: 'For Real Estate and Relocation',
      text: 'Explore how crime mapping and neighborhood intelligence can support home buyers, agents, and relocation professionals.',
      link: '/intelligence_products',
      button: 'View report options',
    },
  ];

  const openComingSoon = (title) => {
    setModalTitle(title);
    setModalOpen(true);
  };

  const seoTopics = [
    'OSINT for executive protection',
    'What is doxxing',
    'Online exposure assessment',
    'Data broker removal planning',
    'Swatting risk indicators',
    'Travel risk intelligence brief',
    'Crime mapping for home buyers',
    'Open-source intelligence tools',
    'Protective intelligence reports',
    'Event security OSINT',
    'Threat monitoring for executives',
    'Residential crime intelligence',
  ];

  return (
    <>
      <Headercomponent />

      <main className="container">
        <div className="hero">
          <NodeBackground />
          <div className="hero-content">
            <p className="eyebrow">Resources</p>
            <h1>OSINT, Protective Intelligence, and Risk Resources</h1>
            <p>
              Practical guides, tool explainers, sample reports, and client-specific articles for executives,
              protective teams, family offices, real estate clients, investigators, and organizations trying to
              understand public risk before making decisions.
            </p>
            <div className="hero-actions">
              <Link to="/resources/what-is-osint" className="cta-button">Start with OSINT</Link>
              <Link to="/intelligence-products" className="cta-button">View Intelligence Products</Link>
            </div>
          </div>

          <div className="hero-diagram">
            <div className="resource-search-card">
              <span className="resource-card-kicker">Popular searches</span>
              <h3>What clients want to understand</h3>
              <div className="resource-search-list">
                <span>What is OSINT?</span>
                <span>How do I reduce online exposure?</span>
                <span>What public information is visible about me?</span>
                <span>How can crime data support a property decision?</span>
              </div>
            </div>
          </div>
        </div>

        <section className="platform-overview">
          <div className="section-heading-row">
            <div>
              <p className="eyebrow">Start here</p>
              <h2>High-Intent Resources for New Visitors</h2>
            </div>
            <Link to="/contact" className="text-link">Ask an analyst →</Link>
          </div>

          <div className="resource-feature-grid">
            {featuredResources.map((resource) => (
              <div
                className="resource-feature-card"
                key={resource.title}
                role="button"
                tabIndex={0}
                onClick={() => openComingSoon(resource.title)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') openComingSoon(resource.title);
                }}
                style={{ cursor: 'pointer' }}
              >
                <span>{resource.label}</span>
                <h3>{resource.title}</h3>
                <p>{resource.description}</p>
                <strong>{resource.button} →</strong>
              </div>
            ))}
          </div>
        </section>

        {guideCollections.map((collection) => (
          <section className="platform-overview" key={collection.title}>
            <div className="resource-section-intro">
              <p className="eyebrow">{collection.eyebrow}</p>
              <h2>{collection.title}</h2>
              <p>{collection.intro}</p>
            </div>

            <div className="article-card-grid resource-article-grid">
              {collection.articles.map((article) => (
                <article className="resource-article-card" key={article.title}>
                  <span className="article-category">{article.audience}</span>
                  <h3>{article.title}</h3>
                  <p>{article.summary}</p>
                  <button
                    className="text-link"
                    onClick={() => openComingSoon(article.title)}
                    style={{
                      cursor: 'pointer',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      font: 'inherit'
                    }}
                  >
                    Read guide →
                  </button>
                </article>
              ))}
            </div>
          </section>
        ))}

        <section className="osint-tools-section">
          <div className="tool-disclaimer-card">
            <p className="eyebrow">OSINT tools</p>
            <h2>Free OSINT Tool Resources</h2>
            <p>
              These articles can explain publicly available OSINT tools and workflows for education. Weathervane Intel
              did not create, own, or maintain the third-party tools discussed in these guides. Availability, pricing,
              and terms can change, and users should review each tool's rules before use.
            </p>
          </div>

          <div className="tool-card-grid">
            {osintTools.map((tool) => (
              <article className="tool-card" key={tool.title}>
                <h3>{tool.title}</h3>
                <p>{tool.summary}</p>
                <Link to={tool.path} className="text-link">View tools article →</Link>
              </article>
            ))}
          </div>
        </section>

        <section className="professional-resource-section">
          <div className="section-heading-row">
            <div>
              <p className="eyebrow">Resources by client type</p>
              <h2>Find the Content Most Relevant to Your Role</h2>
            </div>
            <Link to="/industries" className="text-link">View all industries →</Link>
          </div>

          <div className="professional-card-grid">
            {professionalSections.map((section) => (
              <article className="professional-card" key={section.title}>
                <h3>{section.title}</h3>
                <p>{section.text}</p>
                <Link to={section.link} className="service-detail-button">{section.button}</Link>
              </article>
            ))}
          </div>
        </section>

<section className="process-deliverables-section">
  <div className="seo-copy-block">
    <p className="eyebrow">Topic library</p>
    <h2>Structured Intelligence Topics</h2>
    <p>
      These topics represent common areas of inquiry across executive protection, OSINT analysis,
      exposure assessment, and travel or event risk. Detailed briefings are available upon request
      or through analyst consultation.
    </p>
  </div>

  <div className="seo-topic-grid">
    {seoTopics.map((topic) => (
      <button
        key={topic}
        className="seo-topic-pill"
        onClick={() => openComingSoon(topic)}
        style={{
          cursor: 'pointer',
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.10)',
          color: 'inherit'
        }}
      >
        {topic}
      </button>
    ))}
  </div>
</section>

        <section className="process-deliverables-section">
          <div>
            <p className="eyebrow">From education to action</p>
            <h2>When Free Research Is Not Enough</h2>
            <p>
              Free guides can help clients understand the problem. Weathervane Intel reports, SaaS monitoring,
              and analyst services help them answer the actual question: what matters, what should be done next,
              and what risk indicators deserve attention.
            </p>
          </div>
          <div className="conversion-link-grid">
            <Link to="/intelligence-products">Review intelligence products →</Link>
            <Link to="/product">Explore the SaaS platform →</Link>
            <Link to="/services">View analyst services →</Link>
          </div>
        </section>

        <section className="cta-section resources-cta">
          <h2>Need an Intelligence Answer You Cannot Find Online?</h2>
          <p>
            Weathervane Intel creates tailored reports for travel, property decisions, executive protection,
            event planning, online exposure, and unique OSINT questions.
          </p>
          <Link to="/contact" className="cta-button">Contact an Analyst</Link>
        </section>

        {modalOpen && (
          <div
            className="modal-overlay"
            onClick={() => setModalOpen(false)}
          >
            <div
              className="modal-container"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <div className="modal-eyebrow">Resource status</div>

              <h2>In Development</h2>

              <p>
                <strong>{modalTitle}</strong> is currently in development.
              </p>

              <p>
                We are expanding this section with additional executive intelligence and OSINT educational material.
                If you need this information urgently, it can be provided directly through an analyst briefing.
              </p>

              <div style={{ display: 'flex', gap: '10px', marginTop: '1rem' }}>
                <button
                  className="modal-button"
                  onClick={() => setModalOpen(false)}
                >
                  Close
                </button>

                <a
                  href="/contact"
                  className="modal-button"
                  style={{ textDecoration: 'none', display: 'inline-block' }}
                >
                  Request briefing
                </a>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}

export default Resources;
